import React, {Fragment} from "react";
import {NextPage} from "next";
import {domainUrl} from "../helper";
import {useRouter} from "next/router";
import ShopPageCart from "../components/shop/ShopPageCart";
import Head from "next/head";
import {layoutProps} from "../services/layoutProps";
import {CartPageTranslations} from "../services/translationKeys";
import {TranslationsType} from "../types/homePageTypes";
import MainLayout, {ICurrencies, ILayout} from "../components/ForCWV/Wrappers/MainLayout";

interface IProps extends ILayout {
    dbName: string
    domain: string
    breadcrumb: Array<{ title: string; url: string }>;
    translationsData: TranslationsType,
    selectedLocale: string,
    currencies: Array<ICurrencies>;
    selectedCurrItems: {
        selectedCurrCode: string;
        selectedCurrRate: string;
        selectedCurrSymbol: string
    };
    locale: string;
}

const Cart: NextPage<IProps> = (pageProps) => {
    const {
        selectedLocale,
        dbName,
        translationsData,
        breadcrumb,
        locale,
        currencies,
        backOrderValue,
        domain,
        defaultLangCode,
        locales
    } = pageProps
    const router = useRouter();
    const meta_title = translationsData.ShoppingCart
    const meta_description = "This product has been successfully added to the shopping cart. You can change the amount by increasing or decreasing it."
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)
    const companyNameConverter = " | " + domain.charAt(0).toUpperCase() + domain.slice(1).replace(/[-_]/g, ' ');

    return (
        <MainLayout {...pageProps} >
            <Fragment>
                <Head>
                    <title>{meta_title}</title>
                    <meta name="title" content={meta_title + companyNameConverter}/>
                    <meta name="description" content={meta_description}/>
                    <meta name="generator" content="Zegashop.com"/>
                    <meta name="twitter:description" content={meta_description}/>
                    <meta name="twitter:title" content={meta_title}/>
                    <meta property="og:description" content={meta_description}/>
                    <meta property="og:title" content={meta_title}/>
                    <meta property="og:url" content={meta_url}/>
                    <meta property="og:locale" content={selectedLocale}/>
                    <link rel="canonical" href={meta_url}/>
                    {locales?.length > 0 ? (
                        <>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                            <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                            {locales.map(intl => (
                                <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                            ))}
                        </>
                    ) : null}
                </Head>
                <ShopPageCart
                    dbName={dbName}
                    breadcrumb={breadcrumb}
                    translationsData={translationsData}
                    locale={locale}
                    currencies={currencies}
                    backOrderValue={backOrderValue}
                />
            </Fragment>
        </MainLayout>
    );
}


export async function getServerSideProps({locale, req}: any) {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: CartPageTranslations,
        ipv4: typedReq.ipv4,
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, ShoppingCart: ShoppingCartIntl} } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: ShoppingCartIntl, url: ``}
    ]
    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            activeMobileNavElement: 1,
            ...wrapperProps
        },
    };
}

export default Cart;
